import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarPlus } from "lucide-react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";

import { toMonth } from "../providers/commons/financeSchedule";
import type { CrmDataProvider } from "../providers/types";
import { useFinanceErrorHandler } from "./useFinanceErrorHandler";

const GENERATED_RESOURCES = ["finance_charges", "finance_expenses"];

/** Creates the charges and recurring expenses of the current month */
export const GenerateMonthButton = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const onError = useFinanceErrorHandler();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: () => dataProvider.generateFinanceMonth(toMonth(new Date())),
    onSuccess: async ({ charges, expenses, failed }) => {
      await Promise.all(
        GENERATED_RESOURCES.map((resource) =>
          queryClient.invalidateQueries({ queryKey: [resource] }),
        ),
      );
      notify(
        charges + expenses === 0
          ? "crm.finance.generate.nothing"
          : "crm.finance.generate.done",
        {
          type: failed > 0 ? "warning" : "info",
          messageArgs: { charges, expenses, failed },
        },
      );
    },
    onError,
  });

  return (
    <Button
      type="button"
      variant="outline"
      onClick={() => mutate()}
      disabled={isPending}
    >
      <CalendarPlus />
      {translate("crm.finance.generate.label")}
    </Button>
  );
};
